/* eslint-disable @typescript-eslint/no-explicit-any */

import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

// Components
import { useColumnFilter } from "./hooks/useColumnFilter";
import { useUiSetting } from "./hooks/useUiSetting";
import SntConfigColumns from "./SntConfigColumns";
import { SntTableViewCard } from "./SntTableViewCard";
import { useClientSideData } from "./hooks/useClientSideData";
import type { ColumnFilterSettings } from "@/types/ColumnTypes";

interface SntClientTableViewCardProps {
  allColumns: any[];
  visibleCols?: string[];
  noDataText?: string;
  pageName?: string;
  search?: string;
  searchKeys?: string[];
  isFloatingFooter?: boolean;
  initialPageSize?: number;
  onInit?: () => void;
  isShowConfigColumn?: boolean;
  isDisabbledAddFilter?: boolean;
  onFilterClicked?: () => void;
  fixedSetting?: any;
}

// eslint-disable-next-line react/display-name
const SntClientTableViewCard = forwardRef<any, SntClientTableViewCardProps>(
  (
    {
      allColumns,
      visibleCols,
      noDataText,
      pageName,
      search,
      searchKeys,
      isFloatingFooter,
      initialPageSize = 25,
      onInit,
      isShowConfigColumn = true,
      isDisabbledAddFilter = true,
      onFilterClicked,
      fixedSetting,
    },
    ref
  ) => {
    const [isShowConfigDialog, setShowConfigDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any[]>([]);

    const columnsDef = allColumns;
    const customPageName = pageName;
    const { setting, updateSetting } = useUiSetting(
      customPageName,
      fixedSetting
    );

    const dataFilter = useColumnFilter(
      columnsDef,
      {
        defaultVisibleColumnKeys: visibleCols ?? [],
        defaultPageSize: initialPageSize,
      },
      setting as ColumnFilterSettings
    );
    const { filter, refresh, setVisibleColumnKeys, setSortByKey } = dataFilter;

    const { data: dataTable } = useClientSideData(
      dataFilter as any,
      data,
      search,
      searchKeys
    );

    useEffect(() => {
      if (filter && filter.sortBy) {
        updateSetting(filter as any);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    useEffect(() => {
      onInit?.();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Expose available functions for parents
    useImperativeHandle(ref, () => ({
      refresh: () => {
        refresh?.();
      },
      setLoading: (value: any) => {
        setLoading(value);
      },
      setData: (data: any) => {
        setData(data);
      },
      removeItem: (item: any) => {
        if (data) {
          const list = data.filter((row: any) => row.id !== item.id);
          setData(list);
        }
      },
      updateItem: (item: any) => {
        if (data) {
          const findItem = data.find((row: any) => row.id === item.id);
          const list = data.filter((row: any) => row.id !== item.id);
          if (findItem) {
            findItem.active = item.active;
            list.push(findItem);
          }

          setData(list);
        }
      },
      setVisibleColumnKeys: (keys: any) => {
        setVisibleColumnKeys?.(keys);
      },
      setSortByKey: (key: any, desc: any) => {
        setSortByKey?.(key, desc);
      },
    }));

    let onConfigColumn = null;
    if (isShowConfigColumn) {
      onConfigColumn = () => {
        setShowConfigDialog(true);
      };
    }

    if (!dataFilter.filter) return <></>;

    return (
      <div ref={ref}>
        {isShowConfigDialog && (
          <SntConfigColumns
            dataFilter={{...dataFilter, allColumns: dataFilter.allColumns || [], defaultVisibleColumnKeys: dataFilter.defaultVisibleColumnKeys || []} as any}
            isShowConfigDialog={true}
            onDone={() => setShowConfigDialog(false)}
            onCancel={() => setShowConfigDialog(false)}
            isDisabbledGroup={true}
          />
        )}
        <SntTableViewCard
          dataFilter={dataFilter}
          data={dataTable}
          onConfigColumn={onConfigColumn}
          loading={loading}
          noDataText={noDataText}
          isFloatingFooter={isFloatingFooter}
          isDisabbledGroup={true}
          isDisabbledAddFilter={isDisabbledAddFilter}
          onFilterClicked={onFilterClicked}
        />
      </div>
    );
  }
);

export default SntClientTableViewCard;
